import { apiClient } from '../api-client';
import { ApiResponse } from '../../dtos/common/api.dto';
import { AccessReviewDownloadResponse, UserApiDto, UserData } from '../../dtos/admin/user.dto';
import { UserFormData } from '../../types/admin/user'; // UI-specific type stays in types

/**
 * Response interface for user list endpoint
 */
interface FetchUsersResponse {
  users?: UserApiDto[];
  totalCount?: number;
  total?: number;
  pagination?: {
    totalCount?: number;
  };
}

/** Single-user endpoints return the user directly or wrapped in `user` */
type UserResponse = UserApiDto & { user?: UserApiDto };

const KNOWN_STATUSES = ['ACTIVE', 'INACTIVE', 'REVOKED'];

/** Uppercase the status; unknown values are shown as INACTIVE */
function normalizeStatus(status: string | undefined): string {
  const normalized = (status || 'ACTIVE').toUpperCase();
  return KNOWN_STATUSES.includes(normalized) ? normalized : 'INACTIVE';
}

/** Map an API user to the frontend model. Legacy `pii.license` is read as licenseNumber. */
export function mapApiUser(user: UserApiDto): UserData {
  const pii = user.pii || {};
  const licenseNumber = user.licenseNumber ?? pii.licenseNumber ?? pii.license ?? null;
  return {
    id: user.userId || user.id || '',
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    email: user.email || '',
    groups: user.roles || [],
    isProvider: !!user.providerType,
    providerType: user.providerType || null,
    license: licenseNumber,
    licenseNumber,
    licenseExpiry: user.licenseExpiry ?? pii.licenseExpiry ?? null,
    npi: pii.npi || null,
    phone: pii.phone || null,
    status: normalizeStatus(user.status),
    photoUrl: user.photoUrl,
    lastLoginAt: user.lastLoginAt ?? null,
    cognitoStatus: user.cognitoStatus ?? null,
    invitePending: typeof user.invitePending === 'boolean' ? user.invitePending : null,
    clinicianCode: user.clinicianCode ?? null,
  };
}

/** Statuses the backend accepts on create/update (it returns 400 INVALID_STATUS for anything else, incl. Revoked) */
const WRITABLE_STATUSES = ['ACTIVE', 'INACTIVE'];

/**
 * Request body for create/update. `status` is sent only when it is ACTIVE or INACTIVE, so a
 * REVOKED user's status is never sent back (the backend would reject it).
 */
export function toUserPayload(userData: UserFormData) {
  const statusValue = (userData.status || 'ACTIVE').toUpperCase();
  const status = WRITABLE_STATUSES.includes(statusValue) ? statusValue : undefined;
  const licenseNumber = userData.licenseNumber || undefined;
  return {
    email: userData.email,
    firstName: userData.firstName,
    lastName: userData.lastName,
    roles: userData.groups,
    ...(status ? { status } : {}),
    providerType: userData.isProvider ? userData.providerType : null,
    pii: userData.isProvider ? {
      phone: userData.phone,
      licenseNumber,
      // legacy key, kept so an older backend still stores the number
      license: licenseNumber,
      licenseExpiry: userData.licenseExpiry || null,
      npi: userData.npi,
    } : undefined,
  };
}

/**
 * Fetch all users with optional filtering
 */
export const fetchUsers = async (
  page: number = 1,
  limit: number = 100,
  search?: string,
  status?: string,
  providerType?: string
): Promise<{ users: UserData[], totalCount: number }> => {
  try {
    const params: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString(),
    };

    if (search) {
      params.search = search;
    }

    if (status && status !== 'All') {
      params.status = status;
    }

    if (providerType && providerType !== 'All') {
      if (providerType === 'None') {
        params.isProvider = 'false';
      } else {
        params.providerType = providerType;
      }
    }

    const response: ApiResponse<FetchUsersResponse | UserApiDto[]> = await apiClient.get('/users', params);

    if (response.success && response.data) {
      const data = response.data;
      const apiUsers: UserApiDto[] = Array.isArray(data)
        ? data
        : Array.isArray(data.users) ? data.users : [];

      const users = apiUsers.map(mapApiUser);
      const totals = Array.isArray(data) ? undefined : data;

      return {
        users,
        totalCount: totals?.totalCount || totals?.total || totals?.pagination?.totalCount || users.length
      };
    }

    throw new Error(response.error?.message || 'Failed to fetch users');
  } catch (error) {
    console.error('Error fetching users:', error instanceof Error ? error.message : 'unknown error');
    throw error;
  }
};

/**
 * Fetch a single user by ID
 */
export const fetchUserById = async (userId: string): Promise<UserData> => {
  try {
    const response: ApiResponse<UserResponse> = await apiClient.get(`/users/${userId}`, { includePII: 'true' });

    if (response.success && response.data) {
      return mapApiUser(response.data.user || response.data);
    }

    throw new Error(response.error?.message || 'Failed to fetch user');
  } catch (error) {
    console.error('Error fetching user:', error instanceof Error ? error.message : 'unknown error');
    throw error;
  }
};

/**
 * Create a new user
 */
export const createUser = async (userData: UserFormData): Promise<UserData> => {
  try {
    const response: ApiResponse<UserResponse> = await apiClient.post('/users', toUserPayload(userData));

    if (response.success && response.data) {
      return mapApiUser(response.data.user || response.data);
    }

    throw new Error(response.error?.message || 'Failed to create user');
  } catch (error) {
    console.error('Error creating user:', error instanceof Error ? error.message : 'unknown error');
    throw error;
  }
};

/**
 * Update an existing user
 */
export const updateUser = async (userId: string, userData: UserFormData): Promise<UserData> => {
  try {
    const response: ApiResponse<UserResponse> = await apiClient.put(`/users/${userId}`, toUserPayload(userData));

    if (response.success && response.data) {
      return mapApiUser(response.data.user || response.data);
    }

    throw new Error(response.error?.message || 'Failed to update user');
  } catch (error) {
    console.error('Error updating user:', error instanceof Error ? error.message : 'unknown error');
    throw error;
  }
};

/**
 * Delete a user (may not be used if deletion is handled differently)
 */
export const deleteUser = async (userId: string): Promise<void> => {
  try {
    const response: ApiResponse<void> = await apiClient.delete(`/users/${userId}`);

    if (response.success) {
      return;
    }

    throw new Error(response.error?.message || 'Failed to delete user');
  } catch (error) {
    console.error('Error deleting user:', error instanceof Error ? error.message : 'unknown error');
    throw error;
  }
};

/**
 * Resend the Cognito invite to a user whose invite is still pending (FORCE_CHANGE_PASSWORD).
 * 409 INVITE_NOT_PENDING when the user already set a password.
 */
export const resendUserInvite = async (userId: string): Promise<void> => {
  await apiClient.post(`/users/${userId}/invite/resend`, {});
};

/**
 * Revoke a pending invite: the Cognito user is deleted and the user becomes Revoked.
 * 409 INVITE_NOT_PENDING when the user already confirmed.
 */
export const revokeUserInvite = async (userId: string): Promise<void> => {
  await apiClient.post(`/users/${userId}/invite/revoke`, {});
};

function saveBlob(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Download the access-review CSV (owner/admin). The backend returns a short-lived download URL
 * like the audit log export does; an inline CSV body is also accepted.
 */
export const downloadAccessReview = async (): Promise<void> => {
  const response: ApiResponse<AccessReviewDownloadResponse> = await apiClient.get('/users/access-review');
  const data = response.data;
  const fileName = data?.fileName || `access-review-${new Date().toISOString().slice(0, 10)}.csv`;
  const inline = data?.csv ?? data?.content;
  if (typeof inline === 'string') {
    saveBlob(new Blob([inline], { type: 'text/csv;charset=utf-8' }), fileName);
    return;
  }
  if (!data?.downloadUrl) {
    throw new Error('The access review did not return a download');
  }
  // Pre-signed S3 URL: same handling as AuditLogService.downloadFile
  const fileResponse = await fetch(data.downloadUrl);
  if (!fileResponse.ok) {
    throw new Error('Failed to download the access review');
  }
  saveBlob(await fileResponse.blob(), fileName);
};

/**
 * Fetch all providers (users with provider type)
 */
export const fetchAllProviders = async (): Promise<UserData[]> => {
  try {
    const result = await fetchUsers(1, 10, undefined, 'ACTIVE', 'All');

    // Filter to only include users who are providers
    const providers = result.users.filter(user => user.isProvider === true);

    // Map provider types to visit disciplines (LVN and other nurse types → RN)
    return providers.map(provider => {
      let providerType = provider.providerType;
      if (providerType?.includes('Nurse') || providerType?.includes('RN') || providerType?.includes('LVN')) {
        providerType = 'RN';
      }
      return {
        ...provider,
        providerType: providerType || 'Unknown'
      };
    });
  } catch (error) {
    console.error('Error fetching providers:', error instanceof Error ? error.message : 'unknown error');
    throw error;
  }
};
